import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().oauth.auth0Token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
});

// Wait for the auth token to appear in the Redux store, up to a timeout.
const waitForToken = async (api, timeoutMs = 5000) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const token = api.getState().oauth?.auth0Token;
    if (token) return token;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return null;
};

// Create a wrapper around the base query to handle token refreshes
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Wait for the token to be set before firing the request (handles initial-load race)
  if (!api.getState().oauth?.auth0Token) {
    await waitForToken(api);
  }

  let result = await baseQuery(args, api, extraOptions);

  const state = api.getState();
  const lastTokenUpdateTime = state.oauth.lastTokenUpdateTime;
  const isTokenRecentlyUpdated = lastTokenUpdateTime && (Date.now() - lastTokenUpdateTime) < 20000;

  // If the request failed with a 401 and token was recently updated, retry the request
  if ((result.error?.status === 401 || result.error?.status === 403) && isTokenRecentlyUpdated) {
    // Retry the request with the new token
    result = await baseQuery(args, api, extraOptions);
  }

  return result;
};

// Create a wrapper that retries failed requests up to 3 times.
// Endpoints can opt out (or down) via extraOptions.maxRetries — e.g. the portal's
// agent turns and rule-set authoring are expensive LLM calls with side effects
// (a blind retry appends duplicate conversation messages / re-runs the drafting),
// and deliberate 409/422 gates never succeed on retry.
const baseQueryWithRetry = async (args, api, extraOptions) => {
  let retries = 0;
  const maxRetries = extraOptions?.maxRetries ?? 3;
  if (maxRetries === 0) {
    return baseQueryWithReauth(args, api, extraOptions);
  }

  while (retries < maxRetries) {
    const result = await baseQueryWithReauth(args, api, extraOptions);
    
    // If the request was successful or it's a 401/403 error (handled by baseQueryWithReauth), return the result
    if (!result.error || result.error?.status === 401 || result.error?.status === 403) {
      return result;
    }
    
    // For other errors, retry up to maxRetries times
    retries++;
    if (retries === maxRetries) {
      return result;
    }
    
    // Wait for a short time before retrying (exponential backoff)
    await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
  }
};

export default baseQueryWithRetry;